import React, { ChangeEvent, useEffect, useState } from 'react';
import cx from 'classnames';
import styles from './actionbar.module.scss';
import Tooltip from '../tooltip';
import { useDispatch } from 'react-redux';
import { toggleSeek } from 'src/redux/actions/seek';

const SeekIcon = '/images/seek.gif';

interface ActionsContainerProps {
  children: React.ReactNode;
}

const ActionsContainer = ({ children }: ActionsContainerProps) => {
  return <div className={styles.container}>{children}</div>;
};

interface ActionRowProps {
  className?: string;
  children: React.ReactNode;
}

const ActionRow = ({ className, children }: ActionRowProps) => {
  return <div className={cx(styles.row, className)}>{children}</div>;
};

interface ActionIconButtonProps {
  icon: string;
  className?: string;
  tooltip?: string;
  disabled?: boolean;
  testId?: string;
  // eslint-disable-next-line no-unused-vars
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  scale?: number
}

const ActionIconButton = ({ icon, className, disabled, tooltip, onClick, scale = 1, testId }: ActionIconButtonProps) => {
  const itemWidth = 40;
  return (
    <Tooltip content={tooltip} placement='bottom'>
      <button style={{ width: `${itemWidth * scale}px` }} disabled={disabled} 
        className={cx(styles.item, styles.iconItem, className)} 
        onClick={onClick}
        data-testid={testId}
      >
        <i className={cx(styles.icon, icon)}></i>
      </button>
    </Tooltip>
  );
};

interface ActionItemProps {
  className?: string;
  tooltip?: string;
  children: React.ReactNode;
  // eslint-disable-next-line no-unused-vars
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
}

const ActionItem = ({ children, className, tooltip, onClick }: ActionItemProps) => {
  return (
    <Tooltip content={tooltip} placement='bottom'>
      <div className={cx(styles.item, className)} onClick={onClick}>
        {children}
      </div>
    </Tooltip>
  );
};

interface ActionButtonProps {
  icon?: string;
  image?: string;
  title: string;
  className?: string;
  disabled?: boolean;
  width?: 1 | 2 | 3 | 4 | 5 | 6;
  variant?: 'gradient';
  testId?: string;
  // eslint-disable-next-line no-unused-vars
  onClick: (e: React.MouseEvent<HTMLElement>) => void
}

const ActionButton = ({ icon, image, title, disabled, className, width = 1, variant, testId, onClick }: ActionButtonProps) => {
  return (
    <button className={cx(styles.item, styles.buttonItem, styles[`width-${width}`], className, {
      [styles.gradient]: variant === 'gradient'
    })}
      disabled={disabled}
      data-testid={testId}
      onClick={onClick}>
      {icon &&
        <i className={cx(styles.icon, icon, {
          [styles.gradientIcon]: variant === 'gradient'
        })}></i>
      }
      {image && 
        <img src={image} alt="" className={styles.image} />
      }
      <span className={styles.title}>{title}</span>
    </button>
  );
};

interface ActionSearchProps {
  query: string | null;
  // eslint-disable-next-line no-unused-vars
  onSearch: (query: string | null) => void;
  disabled?: boolean;
}
const ActionSearch = ({ query, onSearch, disabled = false }: ActionSearchProps) => {
  const [value, setValue] = useState<string>("");

  useEffect(() => {
    setValue(query || "");
  }, [query]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    setValue(text);
    if (text === "") {
      onSearch(null);
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSearch(value ? value.trim() : null);
  };

  const handleClear = () => {
    setValue("");
    onSearch(null);
  };

  return (
    <div className={cx(styles.item, styles.searchBar)}>
      <i className={cx(styles.icon, "paperflite-search")}></i>
      <form onSubmit={handleSubmit}>
        <input
          type="text"
          placeholder="Type here to search..."
          value={value}
          onChange={handleChange}
          disabled={disabled}
          className={cx({
            [styles.disabled]: disabled,
          })}
        />
        {value && (
          <i
            className={cx(
              styles.icon,
              styles.clear,
              "paperflite-close",
            )}
            onClick={handleClear}
          ></i>
        )}
      </form>
    </div>
  );
};

interface ActionDropdownProps {
  children: React.ReactNode;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}


const ActionDropdown = ({ children, onMouseEnter, onMouseLeave }: ActionDropdownProps) => {
  return (
    <div className={styles.dropdown} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      {children}
    </div>
  );
};

interface ActionDropdownToggleProps {
  className?: string;
  icon?: string;
  text?: string;
  onToggle?: () => void;
  onMouseEnter?: () => void;
  // eslint-disable-next-line no-unused-vars
  onMouseLeave?: (e: React.MouseEvent<HTMLElement>) => void;
}

const ActionDropdownToggle = ({ className, icon, text, onToggle, onMouseEnter, onMouseLeave }: ActionDropdownToggleProps) => {
  return (
    <div className={cx(styles.dropdown, className)}>
      <div className={cx(styles.item, styles.iconItem)} onClick={onToggle} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
        {icon && <i className={cx(styles.icon, icon)}></i>}
        {text && <span className={styles.content}>{text}</span>}
      </div>
    </div>
  );
};

interface ActionDropdownMenuProps {
  show: boolean;
  className?: string;
  children: React.ReactNode;
}

const ActionDropdownMenu = ({ show, className, children }: ActionDropdownMenuProps) => {
  return <div>{show && <div className={cx(styles.menu, className)}>{children}</div>}</div>;
};

interface ActionDropdownMenuItemProps {
  icon?: string;
  text?: string;
  isLogo?: boolean;
  className?: string;
  onClick?: () => void;
}

const ActionDropdownMenuItem = ({ icon, text, isLogo, onClick, className }: ActionDropdownMenuItemProps) => {
  return (
    <div onClick={onClick} className={cx(styles.menuItem, className)}>
      {!isLogo && icon && <i className={cx(styles.icon, icon)} />}
      {isLogo && <img src={icon} width={16} height={16} className={styles.icon} />}
      {text && <span className={styles.content}>{text}</span>}
    </div>
  );
}

interface ActionGroupProps {
  icon: string,
  children: React.ReactElement<ActionIconButtonProps>[] | React.ReactElement<ActionIconButtonProps>;
}

const ActionGroup = ({ icon, children }: ActionGroupProps) => {
  const [hover, setHover] = useState<boolean>(false);

  const handleMouseEnter = () => {
    setHover(true);
  }

  const handleMouseLeave = () => {
    setHover(false);
  }

  useEffect(() => {
    const timeout = setTimeout(() => {
      setHover(false);
    }, 500);

    return () => {
      clearTimeout(timeout);
    }
  }, [hover]);

  return (
    <div className={cx(styles.item, styles.group)} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <div className={styles.groupIcon}>
        <i className={cx(styles.icon, icon)}></i>
      </div>
      <div className={cx(styles.groupItems, { [styles.disabled]: hover })}>
        {children}
      </div>
    </div>
  );
}

interface ActionFileUploadProps {
  icon?: string
  title?: string,
  tooltip?: string;
  className?: string;
  disabled?: boolean;
  accept?: string;
  width?: 1 | 2 | 3 | 4 | 5 | 6;
  // eslint-disable-next-line no-unused-vars
  onChange: (e: ChangeEvent<HTMLInputElement>) => void
  // eslint-disable-next-line no-unused-vars
  onClick?: (e: any) => void
}

const ActionFileUploadButton: React.FC<ActionFileUploadProps> = ({ icon, title, tooltip, accept, disabled, className, width = 1, onChange, onClick }) => {
  return (
    <Tooltip content={tooltip} placement='bottom'>
      <button className={cx(styles.item, styles.buttonItem, styles.fileUpload, styles[`width-${width}`], className)}
        disabled={disabled}>
        {icon &&
          <i className={cx(styles.icon, icon)}></i>
        }
        {title &&
          <span className={styles.title}>{title}</span>
        }
        <input type="file" title='' accept={accept} disabled={disabled} onChange={onChange} onClick={onClick}></input>
      </button>
    </Tooltip>
  );
};

interface SetItemProps {
  icon: string;
  className?: string;
  tooltip?: string;
  disabled?: boolean;
  // eslint-disable-next-line no-unused-vars
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
}

const SetItem = ({ icon, className, disabled, tooltip, onClick }: SetItemProps) => {
  return (
    <Tooltip content={tooltip} placement='bottom'>
      <button disabled={disabled} className={cx(styles.setItem, className)} onClick={onClick}>
        <i className={cx(styles.icon, icon)}></i>
      </button>
    </Tooltip>
  );
};

interface ActionSetProps {
  className?: string;
  children: React.ReactNode;
}

const ActionSet: React.FC<ActionSetProps> = ({ className, children }) => {
  return (
    <div className={cx(styles.set, className)}>
      {children}
    </div>
  )
}

interface SeekProps {
  className?: string;
  scale?: number; 
}

const Seek: React.FC<SeekProps> = ({ className, scale = 1 }) => {
  const dispatch = useDispatch();

  const handleClick = () => {
    dispatch(toggleSeek());
  }

  return (
    <Tooltip content="Seek" placement="bottom">
      <button style={{ width: `${40 * scale}px` }} className={cx(styles.item, styles.iconItem, styles.seek, className)} onClick={handleClick}>
        <img src={SeekIcon} alt="" />
      </button>
    </Tooltip>
  )
}

const Actions = {
  Container: ActionsContainer,
  Row: ActionRow,
  Item: ActionItem,
  IconButton: ActionIconButton,
  Button: ActionButton,
  Group: ActionGroup,
  Search: ActionSearch,
  FileUpload: ActionFileUploadButton,
  Dropdown: ActionDropdown,
  DropdownToggle: ActionDropdownToggle,
  DropdownMenu: ActionDropdownMenu,
  DropdownMenuItem: ActionDropdownMenuItem,
  Set: ActionSet,
  SetItem: SetItem,
  Seek: Seek
};

export default Actions;
